import React, { useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../services/api';
import './MapaOperadores.css';

// Fix para iconos de Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface UbicacionOperador {
  id: number;
  operador_id: number;
  latitud: number;
  longitud: number;
  precision_gps: number | null;
  velocidad: number | null;
  direccion: string | null;
  fecha_hora: string;
  nombres: string;
  apellidos: string;
  numero_documento: string;
  tipo_documento: string;
  operador_estado: string;
}

const MapaOperadores: React.FC = () => {
  const [ubicaciones, setUbicaciones] = useState<UbicacionOperador[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    cargarUbicaciones();
    
    // Auto-refresh cada 30 segundos si está activado
    if (autoRefresh) {
      intervalRef.current = setInterval(() => {
        cargarUbicaciones();
      }, 30000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoRefresh]);

  const cargarUbicaciones = async () => {
    try {
      setLoading(true);
      const response = await api.get('/ubicaciones/todas');
      setUbicaciones(response.data.data || []);
      setError('');
    } catch (err: any) {
      console.error('Error cargando ubicaciones:', err);
      setError(err.response?.data?.error || 'Error al cargar ubicaciones');
    } finally {
      setLoading(false);
    }
  };

  // Calcular centro del mapa basado en las ubicaciones
  const calcularCentro = (): [number, number] => {
    if (ubicaciones.length === 0) {
      // Centro de Colombia por defecto
      return [4.6097, -74.0817];
    }

    const sumLat = ubicaciones.reduce((sum, u) => sum + parseFloat(String(u.latitud)), 0);
    const sumLng = ubicaciones.reduce((sum, u) => sum + parseFloat(String(u.longitud)), 0);
    
    return [
      sumLat / ubicaciones.length,
      sumLng / ubicaciones.length
    ];
  };

  if (loading && ubicaciones.length === 0) {
    return <div className="loading">Cargando ubicaciones...</div>;
  }

  return (
    <div className="mapa-operadores-page">
      <div className="mapa-controls">
        <h2>📍 Ubicaciones de Operadores</h2>
        <div className="controls-actions">
          <button onClick={cargarUbicaciones} className="btn-refresh">
            🔄 Actualizar
          </button>
          <label className="toggle-auto-refresh">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            <span>Actualización automática (30s)</span>
          </label>
          <div className="operadores-count">
            {ubicaciones.length} operador{ubicaciones.length !== 1 ? 'es' : ''} en el mapa
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {ubicaciones.length === 0 ? (
        <div className="empty-state">
          <p>No hay ubicaciones disponibles</p>
          <p className="empty-subtitle">Los operadores deben enviar su ubicación para que aparezcan en el mapa</p>
        </div>
      ) : (
        <div className="mapa-container">
          <MapContainer
            center={calcularCentro()}
            zoom={6}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {ubicaciones.map((ubicacion) => (
              <Marker
                key={ubicacion.id}
                position={[parseFloat(String(ubicacion.latitud)), parseFloat(String(ubicacion.longitud))]}
              >
                <Popup>
                  <div className="popup-content">
                    <h3>{ubicacion.nombres} {ubicacion.apellidos}</h3>
                    <p><strong>Documento:</strong> {ubicacion.tipo_documento} {ubicacion.numero_documento}</p>
                    <p><strong>Última actualización:</strong> {new Date(ubicacion.fecha_hora).toLocaleString()}</p>
                    {ubicacion.velocidad && (
                      <p><strong>Velocidad:</strong> {Math.round(ubicacion.velocidad * 3.6)} km/h</p>
                    )}
                    {ubicacion.precision_gps && (
                      <p><strong>Precisión:</strong> ±{Math.round(ubicacion.precision_gps)}m</p>
                    )}
                    <p className="coordenadas">
                      <strong>Coordenadas:</strong><br />
                      Lat: {parseFloat(String(ubicacion.latitud)).toFixed(6)}<br />
                      Lng: {parseFloat(String(ubicacion.longitud)).toFixed(6)}
                    </p>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      )}
    </div>
  );
};

export default MapaOperadores;

